import React, { useState, useEffect } from "react";
import { Modal, Group, Loader, Table, Badge, Text, ScrollArea, Alert, Button, Anchor, Switch } from "@mantine/core";
import { Link } from "react-router-dom";
import { collection, getDocs, query, where, orderBy, DocumentData, doc, updateDoc } from "firebase/firestore";
import { db } from "../../firebase";
import Handlebars from "handlebars";
import { flattenFlexSubmissionData } from "../../hooks/flexFormUtils";
import { InfoCircle, Refresh, Check } from "tabler-icons-react";
import OptionSyncDiffModal from "../../components/modals/option-sync-diff-modal";
import {
  getSyncDiffForOptionsSet,
  executeSync,
  SyncDiffResult,
} from "../../hooks/useFormOptionsSetBuilder";

export interface SubmissionMappingsModalProps {
  opened: boolean;
  onClose: () => void;
  optionsDoc: DocumentData | null;
  companyId: string;
  onSyncSuccess?: () => void;
}

interface ProcessedMapping {
  id: string;
  documentNumber: string;
  submitterName: string;
  submissionDate: string;
  dateStr: string;
  value: string;
  label: string;
  status: string;
}

export default function SubmissionMappingsModal({
  opened,
  onClose,
  optionsDoc,
  companyId,
  onSyncSuccess,
}: SubmissionMappingsModalProps) {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [processedMappings, setProcessedMappings] = useState<ProcessedMapping[]>([]);
  const [autoSyncEnabled, setAutoSyncEnabled] = useState<boolean>(optionsDoc?.autoSync !== false);
  const [togglingAutoSync, setTogglingAutoSync] = useState<boolean>(false);

  const [diffModalOpen, setDiffModalOpen] = useState(false);
  const [syncDiffResult, setSyncDiffResult] = useState<SyncDiffResult | null>(null);
  const [diffLoading, setDiffLoading] = useState(false);
  const [syncNotification, setSyncNotification] = useState<{ type: "success" | "error"; message: string } | null>(null);

  useEffect(() => {
    setAutoSyncEnabled(optionsDoc?.autoSync !== false);
  }, [optionsDoc]);

  const handleToggleAutoSync = async (checked: boolean) => {
    if (!optionsDoc?.id || togglingAutoSync) return;
    setTogglingAutoSync(true);
    setSyncNotification(null);
    const previous = autoSyncEnabled;
    setAutoSyncEnabled(checked);
    try {
      await updateDoc(doc(db, "flexFormSelectOptions", optionsDoc.id), {
        autoSync: checked,
      });
      setSyncNotification({
        type: "success",
        message: checked
          ? "Live auto-sync enabled. This option set will update automatically on form submission save, edit, or delete."
          : "Live auto-sync disabled. Use 'Sync Options Set' below to synchronize manually.",
      });
      onSyncSuccess?.();
    } catch (err: any) {
      console.error("Failed to toggle autoSync:", err);
      setAutoSyncEnabled(previous);
      setSyncNotification({
        type: "error",
        message: `Failed to update auto-sync setting: ${err.message}`,
      });
    } finally {
      setTogglingAutoSync(false);
    }
  };

  const handleOpenSyncDiff = async () => {
    if (!optionsDoc || !companyId) return;
    setDiffLoading(true);
    setSyncNotification(null);
    setDiffModalOpen(true);
    try {
      const res = await getSyncDiffForOptionsSet(optionsDoc.id, companyId);
      setSyncDiffResult(res.diff);
    } catch (err: any) {
      console.error("Error calculating sync diff:", err);
      setSyncNotification({
        type: "error",
        message: `Failed to calculate sync diff: ${err.message}`,
      });
      setDiffModalOpen(false);
    } finally {
      setDiffLoading(false);
    }
  };

  const handleConfirmSync = async (overrideZeroGuard?: boolean) => {
    if (!optionsDoc || !syncDiffResult) return;
    setDiffLoading(true);
    try {
      const res = await executeSync(optionsDoc.id, syncDiffResult, overrideZeroGuard);
      if (res.success) {
        setSyncNotification({
          type: "success",
          message: `Successfully synchronized "${optionsDoc.name}"! Set now contains ${res.count} options.`,
        });
        setDiffModalOpen(false);
        setSyncDiffResult(null);
        onSyncSuccess?.();
      }
    } catch (err: any) {
      console.error("Sync execution failed:", err);
      setSyncNotification({
        type: "error",
        message: `Sync failed: ${err.message}`,
      });
    } finally {
      setDiffLoading(false);
    }
  };

  const compileTemplate = (templateStr: string | undefined, context: Record<string, any>): string => {
    try {
      if (!templateStr) return "";
      const template = Handlebars.compile(templateStr);
      return template(context).trim();
    } catch (err) {
      console.error("Error compiling Handlebars template:", templateStr, err);
      return "";
    }
  };

  useEffect(() => {
    if (!opened || !optionsDoc || !companyId) return;

    const fetchAndProcess = async () => {
      setLoading(true);
      setError("");
      try {
        const linkedFormTemplateId = optionsDoc.linkedFormTemplateId as string | undefined;
        const mappingConfig = optionsDoc.mappingConfig as { labelTemplate?: string; valueTemplate?: string } | undefined;

        if (!linkedFormTemplateId || !mappingConfig) {
          setError("This options set is not configured for automatic synchronization from submissions.");
          setLoading(false);
          return;
        }

        // Fetch submissions ordered by submissionDate desc
        const qSubmissions = query(
          collection(db, "flexFormSubmissions"),
          where("templateId", "==", linkedFormTemplateId),
          where("companyId", "==", companyId),
          orderBy("submissionDate", "desc")
        );
        const subSnapshot = await getDocs(qSubmissions);
        const submissions = subSnapshot.docs.map((d) => ({ id: d.id, ...d.data() }));

        const seenValues = new Set<string>();
        const mapped: ProcessedMapping[] = submissions.map((sub: any) => {
          const context = flattenFlexSubmissionData(sub);
          const label = compileTemplate(mappingConfig.labelTemplate, context);
          const value = compileTemplate(mappingConfig.valueTemplate, context);

          let status = "Active";
          if (!label || !value) {
            status = "Skipped: Empty value or label";
          } else if (seenValues.has(value)) {
            status = "Overwritten: Duplicate value (older submission)";
          } else {
            seenValues.add(value);
          }

          const submissionDateValue = sub.submissionDate;
          const dateObj = submissionDateValue && typeof submissionDateValue.toDate === "function"
            ? submissionDateValue.toDate()
            : submissionDateValue ? new Date(submissionDateValue) : new Date();
          const yyyy = dateObj.getFullYear();
          const mm = String(dateObj.getMonth() + 1).padStart(2, "0");
          const dd = String(dateObj.getDate()).padStart(2, "0");
          const dateStr = `${yyyy}-${mm}-${dd}`;

          return {
            id: sub.id,
            documentNumber: sub.documentNumber || "No Number",
            submitterName: sub.submitterName || "System",
            submissionDate: (context.submissionDate as string) || "",
            dateStr,
            value,
            label,
            status,
          };
        });

        setProcessedMappings(mapped);
      } catch (err: any) {
        console.error("Error fetching submission mappings:", err);
        setError(err.message || "Failed to load submission mappings.");
      } finally {
        setLoading(false);
      }
    };

    fetchAndProcess();
  }, [opened, optionsDoc, companyId]);

  const getStatusBadge = (status: string) => {
    switch (status) {
      case "Active":
        return <Badge color="green" variant="filled">Active</Badge>;
      case "Overwritten: Duplicate value (older submission)":
        return (
          <Badge color="orange" variant="light" style={{ textTransform: "none" }}>
            Overwritten (Duplicate Value)
          </Badge>
        );
      default:
        return (
          <Badge color="gray" variant="outline" style={{ textTransform: "none" }}>
            Skipped (Empty)
          </Badge>
        );
    }
  };

  return (
    <>
      <Modal
      opened={opened}
      onClose={onClose}
      title={
        <Text size="lg" weight={600}>
          Submission Mappings for "{optionsDoc?.name}"
        </Text>
      }
      size="xl"
    >
      {loading ? (
        <Group position="center" my="xl">
          <Loader />
          <Text size="sm">Analyzing submissions...</Text>
        </Group>
      ) : error ? (
        <Alert color="red" icon={<InfoCircle size={16} />}>
          {error}
        </Alert>
      ) : (
        <div>
          {syncNotification && (
            <Alert
              color={syncNotification.type === "success" ? "green" : "red"}
              icon={syncNotification.type === "success" ? <Check size={16} /> : <InfoCircle size={16} />}
              mb="md"
              withCloseButton
              onClose={() => setSyncNotification(null)}
            >
              {syncNotification.message}
            </Alert>
          )}

          <Group position="apart" mb="md">
            <Switch
              checked={autoSyncEnabled}
              onChange={(e) => handleToggleAutoSync(e.currentTarget.checked)}
              disabled={togglingAutoSync}
              label="Auto-sync on submission save, edit, or delete"
              data-testid="mappings-auto-sync-switch"
            />
            <Badge color={autoSyncEnabled ? "teal" : "gray"} variant="light">
              {autoSyncEnabled ? "Live Auto-Sync Enabled" : "Manual Sync Only"}
            </Badge>
          </Group>

          <Alert color="blue" icon={<InfoCircle size={16} />} mb="md">
            Submissions are listed from newest to oldest. If multiple submissions share the same evaluated option value (e.g. employee code), only the newest one is <b>Active</b> in the options set; older ones are marked as <b>Overwritten</b>.
          </Alert>

          <ScrollArea style={{ height: "55vh" }}>
            <Table striped highlightOnHover verticalSpacing="xs">
              <thead>
                <tr>
                  <th>Doc Number</th>
                  <th>Submitter</th>
                  <th>Submitted</th>
                  <th>Evaluated Value</th>
                  <th>Evaluated Label</th>
                  <th>Status</th>
                </tr>
              </thead>
              <tbody>
                {processedMappings.map((row, idx) => (
                  <tr key={idx}>
                    <td>
                      <Anchor
                        component={Link}
                        to={`/dashboard/form-template-submissions?templateId=${optionsDoc?.linkedFormTemplateId}&from=${row.dateStr}&to=${row.dateStr}&doc=${row.documentNumber}`}
                        size="sm"
                        weight={500}
                        onClick={onClose}
                      >
                        {row.documentNumber}
                      </Anchor>
                    </td>
                    <td><Text size="sm">{row.submitterName}</Text></td>
                    <td><Text size="sm">{row.submissionDate}</Text></td>
                    <td><Text size="sm">{row.value || <span style={{ color: "red", fontStyle: "italic" }}>Empty</span>}</Text></td>
                    <td><Text size="sm">{row.label || <span style={{ color: "red", fontStyle: "italic" }}>Empty</span>}</Text></td>
                    <td>{getStatusBadge(row.status)}</td>
                  </tr>
                ))}
                {processedMappings.length === 0 && (
                  <tr>
                    <td colSpan={6} style={{ textAlign: "center", padding: "2rem" }}>
                      <Text color="dimmed">No submissions found for the source form.</Text>
                    </td>
                  </tr>
                )}
              </tbody>
            </Table>
          </ScrollArea>
          <Group position="apart" mt="md">
            <Group>
              <Button
                color="cap-navy"
                leftIcon={<Refresh size={16} />}
                onClick={handleOpenSyncDiff}
                loading={diffLoading}
                disabled={loading || !!error || !optionsDoc?.linkedFormTemplateId || !optionsDoc?.mappingConfig}
              >
                Sync Options Set
              </Button>
              {optionsDoc?.linkedFormTemplateId && (
                <Anchor
                  component={Link}
                  to={`/dashboard/form-template-submissions?template=${optionsDoc.linkedFormTemplateId}&allTime=true`}
                  data-testid="view-all-submissions-link"
                  onClick={onClose}
                  style={{ textDecoration: "none" }}
                >
                  <Button variant="light" color="cap-blue">
                    View All Submissions (All Time)
                  </Button>
                </Anchor>
              )}
            </Group>
            <Button onClick={onClose} color="gray" variant="default">
              Close
            </Button>
          </Group>
        </div>
      )}
    </Modal>
    <OptionSyncDiffModal
      opened={diffModalOpen}
      onClose={() => {
        if (!diffLoading) {
          setDiffModalOpen(false);
          setSyncDiffResult(null);
        }
      }}
      onConfirm={handleConfirmSync}
      diff={syncDiffResult}
      optionsSetName={optionsDoc?.name || "Options Set"}
      loading={diffLoading}
    />
  </>
  );
}
